import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Package, Plus, Search, X, Pencil, Trash2, History, ImagePlus, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: '', description: '', brand: '', presentation: 'frasco', unit_cost: '', profit_percentage: '', quantity: '', photo: null });
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/operations/products`);
      setProducts(res.data);
    } catch { toast.error('Error al cargar productos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);


  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', brand: '', presentation: 'frasco', unit_cost: '', profit_percentage: '', quantity: '', photo: null });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description, brand: p.brand, presentation: p.presentation || 'frasco', unit_cost: String(p.unit_cost), profit_percentage: String(p.profit_percentage), quantity: String(p.quantity), photo: p.photo });
    setShowForm(true);
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagen máximo 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.name || !form.unit_cost || !form.profit_percentage) { toast.error('Nombre, Costo y % Ganancia son requeridos'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/operations/products/${editing.id}`, {
          name: form.name, description: form.description, brand: form.brand, presentation: form.presentation,
          unit_cost: parseFloat(form.unit_cost), profit_percentage: parseFloat(form.profit_percentage),
          ...(form.photo !== editing.photo ? { photo: form.photo } : {}),
        });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/operations/products`, {
          name: form.name, description: form.description, brand: form.brand, presentation: form.presentation,
          unit_cost: parseFloat(form.unit_cost), profit_percentage: parseFloat(form.profit_percentage),
          quantity: parseInt(form.quantity) || 0, photo: form.photo,
        });
        toast.success('Producto creado');
      }
      setShowForm(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.name}?`)) return;
    try {
      await axios.delete(`${API}/operations/products/${p.id}`);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch { toast.error('Error al eliminar'); }
  };

  const openHistory = async (p) => {
    setHistoryProduct(p);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/operations/products/${p.id}/history`);
      setHistory(res.data);
    } catch { toast.error('Error al cargar historial'); }
    finally { setHistoryLoading(false); }
  };

  const salePrice = form.unit_cost && form.profit_percentage ? round(parseFloat(form.unit_cost) * (1 + parseFloat(form.profit_percentage) / 100)) : '';
  const filtered = products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()) || (p.brand || '').toLowerCase().includes(search.toLowerCase()));

  const movTypeLabel = { orden: 'Orden', muestra: 'Muestra', ajuste: 'Ajuste', aumento: 'Aumento' };
  const movTypeColor = { orden: 'bg-blue-100 text-blue-700', muestra: 'bg-purple-100 text-purple-700', ajuste: 'bg-orange-100 text-orange-700', aumento: 'bg-emerald-100 text-emerald-700' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Productos</h1>
          <p className="text-gray-500 mt-1">Inventario general de productos</p>
        </div>
        <Button onClick={openCreate} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="add-product-btn">
          <Plus size={18} className="mr-2" /> Nuevo Producto
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <Input placeholder="Buscar por nombre, SKU o marca..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9 bg-gray-50 border-gray-200 rounded-xl h-10" data-testid="search-products" />
      </div>

      <Card className="border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-16">Foto</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Marca</TableHead>
              <TableHead className="text-right">Costo</TableHead>
              <TableHead className="text-right">% Gan.</TableHead>
              <TableHead className="text-right">P. Venta</TableHead>
              <TableHead className="text-center">Cantidad</TableHead>
              <TableHead className="text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-12 text-gray-400">
                <Package size={36} className="mx-auto mb-2 opacity-40" />
                <p>{search ? 'Sin resultados' : 'No hay productos registrados'}</p>
              </TableCell></TableRow>
            ) : filtered.map(p => (
              <TableRow key={p.id} className="hover:bg-gray-50" data-testid={`product-row-${p.sku}`}>
                <TableCell>
                  {p.photo ? (
                    <img src={p.photo} alt={p.name} className="w-10 h-10 rounded-lg object-cover border" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Package size={16} className="text-gray-300" /></div>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{p.sku}</Badge></TableCell>
                <TableCell className="font-semibold text-gray-800">{p.name}</TableCell>
                <TableCell>
                  <Badge className={`text-xs ${p.presentation === 'capsula' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-teal-50 text-teal-700 border-teal-200'} border`}>
                    {p.presentation === 'capsula' ? 'Cápsula' : 'Frasco'}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-500 text-sm">{p.brand || '—'}</TableCell>
                <TableCell className="text-right text-sm">${p.unit_cost.toFixed(2)}</TableCell>
                <TableCell className="text-right text-sm">{p.profit_percentage}%</TableCell>
                <TableCell className="text-right font-semibold text-[#467322]">${p.sale_price.toFixed(2)}</TableCell>
                <TableCell className="text-center">
                  <Badge className={`${p.quantity > 10 ? 'bg-emerald-100 text-emerald-700' : p.quantity > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                    {p.quantity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openHistory(p)} title="Info adicional" data-testid={`history-${p.sku}`}><History size={16} className="text-gray-500" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} title="Editar" data-testid={`edit-${p.sku}`}><Pencil size={16} className="text-gray-500" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p)} title="Eliminar"><Trash2 size={16} className="text-red-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{editing ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Nombre *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre del producto" data-testid="input-product-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Marca</Label>
                <Input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="Marca" /></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Presentación *</Label>
              <Select value={form.presentation} onValueChange={v => setForm(f => ({ ...f, presentation: v }))}>
                <SelectTrigger className="bg-gray-50" data-testid="select-presentation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="frasco">Frasco</SelectItem>
                  <SelectItem value="capsula">Cápsula</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Descripción</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción del producto" rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Costo Unitario *</Label>
                <Input type="number" step="0.01" value={form.unit_cost} onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))} placeholder="0.00" data-testid="input-unit-cost" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">% Ganancia *</Label>
                <Input type="number" step="0.1" value={form.profit_percentage} onChange={e => setForm(f => ({ ...f, profit_percentage: e.target.value }))} placeholder="0" data-testid="input-profit-pct" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">P. Venta (auto)</Label>
                <Input value={salePrice ? `$${salePrice}` : ''} disabled className="bg-gray-50 font-semibold text-[#467322]" /></div>
            </div>
            {!editing && (
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Cantidad Inicial</Label>
                <Input type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0" data-testid="input-quantity" /></div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Foto del Producto</Label>
              <div className="flex items-center gap-3">
                {form.photo && <img src={form.photo} alt="preview" className="w-16 h-16 rounded-lg object-cover border" />}
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="text-sm">
                  <ImagePlus size={16} className="mr-2" /> {form.photo ? 'Cambiar' : 'Subir'} Foto
                </Button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="save-product-btn">
              {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear Producto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyProduct} onOpenChange={() => setHistoryProduct(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Info Adicional — {historyProduct?.name} ({historyProduct?.sku})
            </DialogTitle>
          </DialogHeader>
          {historyProduct && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Cantidad Actual</p><p className="font-bold text-lg text-gray-800">{historyProduct.quantity}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Precio de Venta</p><p className="font-bold text-lg text-[#467322]">${historyProduct.sale_price.toFixed(2)}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-gray-400 text-xs">Valor en Inventario</p><p className="font-bold text-lg text-gray-800">${(historyProduct.sale_price * historyProduct.quantity).toFixed(2)}</p></div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-2">Historial de Movimientos</p>
                {historyLoading ? (
                  <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-4 border-[#467322] border-t-transparent" /></div>
                ) : history.length === 0 ? (
                  <p className="text-center text-gray-400 py-6 text-sm">Sin movimientos registrados</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {history.map(m => (
                      <div key={m.id} className={`flex items-center justify-between p-3 rounded-lg border ${m.direction === 'in' ? 'border-emerald-100 bg-emerald-50/50' : 'border-red-100 bg-red-50/50'}`}>
                        <div className="flex items-center gap-3">
                          <Badge className={`text-xs ${movTypeColor[m.movement_type] || 'bg-gray-100 text-gray-700'}`}>{movTypeLabel[m.movement_type] || m.movement_type}</Badge>
                          <span className="text-sm text-gray-600">{m.notes || '—'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-gray-400">{new Date(m.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          <span className={`font-bold ${m.direction === 'in' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {m.direction === 'in' ? '+' : '-'}{m.quantity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

function round(n) { return Math.round(n * 100) / 100; }

export default AdminProductsPage;
